import { useTranslation } from 'react-i18next';
import {
  CartContentBox,
  CartMainContentBox,
} from '@hub/features/purchase/components/cart/cart.styled';
import { PurchaseLayout } from '@hub/features/purchase/purchase.styled';
import { Box, LinearProgress, Typography } from '@mui/material';
import { Skeleton, Stack } from '@monorepo/nova-ui';

/**
 * Billing page skeleton shown behind the OTP modal during quick-purchase flow.
 * Mimics the billing form structure; non-interactive while modal is open.
 * Uses a standalone header (no CartHeader) to avoid FormProvider/useCart dependencies.
 */
export const QuickPurchaseSkeleton = () => {
  const { t } = useTranslation(['purchase']);

  return (
    <PurchaseLayout sx={{ pointerEvents: 'none' }}>
      <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
        <Stack sx={{ flex: 1 }}>
          <Box
            position="sticky"
            display="flex"
            flexDirection="column"
            minHeight={['60px', '78px']}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding={[0, 6]}
              minHeight={['60px', '78px']}
              sx={{ borderBottom: '1px solid #BBBDBF' }}
            >
              <Typography>{t('common.secure_checkout')}</Typography>
            </Box>
            <LinearProgress
              value={75}
              variant="determinate"
              sx={{
                height: 6,
                background: 'transparent',
                '& .MuiLinearProgress-bar': {
                  background:
                    'linear-gradient(to left, #8458FC 0%, #63FFB4 33%, #FFEA75 70%, #FF7B30 100%)',
                },
              }}
            />
          </Box>
          <Stack
            sx={{
              maxHeight: 'calc(100vh - var(--header-height))',
              overflowY: 'auto',
              paddingX: { xs: 4, md: 6, lg: 10, xl: 20, gs: 40 },
              paddingY: { xs: 6, lg: 14, gs: 29 },
              gap: 6,
            }}
          >
            <CartContentBox>
              <CartMainContentBox>
                <Typography variant="h6" fontWeight={400}>
                  {t('billing.title')}
                </Typography>
                <Box display="flex" flexDirection="column" gap={3} width="100%">
                  <Box>
                    <Skeleton
                      variant="text"
                      width="25%"
                      height={20}
                      sx={{ mb: 2 }}
                    />
                    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ flex: 1 }}
                      />
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                    <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ width: 170 }}
                      />
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  </Box>
                  <Box>
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ flex: 1 }}
                      />
                      <Skeleton
                        variant="rounded"
                        height={40}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                    <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                  </Box>
                </Box>
              </CartMainContentBox>
            </CartContentBox>
          </Stack>
        </Stack>
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            minWidth: 353,
            maxWidth: 353,
            backgroundColor: 'background.contrast',
            borderLeft: '1px solid',
            borderColor: 'info.main',
            padding: 6,
          }}
        >
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={80} sx={{ mt: 4 }} />
        </Box>
      </Stack>
    </PurchaseLayout>
  );
};
